import { useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
  useGetGuideItemsQuery,
  useGetRelevanceQuery,
} from '@backrs_ui/grow-ui/src/redux/majorsGuide/majorsGuide.api';

import { CareerRelevancePage } from './CareerRelevancePage';
import { useAgentContext } from './CareerRelevancePageContainer.agentContext';
import { RelevanceMode, RelevanceRating, RelevanceResultItem } from '../types';

const ITEMS_PER_PAGE = 10;
const DEFAULT_LEVELS: RelevanceRating[] = ['High', 'Medium', 'Low'];

/**
 * CareerRelevancePageContainer - Container component for the Career Relevance page
 *
 * Responsibilities:
 * - Parse URL params for mode, selections, primary, levels, page
 * - Call useGetGuideItemsQuery for available items
 * - Call useGetRelevanceQuery for ranked results (skip when no selections)
 * - Handle mode change (clears selections)
 * - Handle selection changes (also clears primary when needed)
 * - Handle level filter toggles
 * - Handle page changes
 * - Handle result click (navigates to detail page)
 * - Render CareerRelevancePage with all data as props
 */
export const CareerRelevancePageContainer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse mode from URL
  const mode: RelevanceMode = useMemo(() => {
    const modeParam = searchParams.get('mode');

    return modeParam === 'majors' ? 'majors' : 'paths';
  }, [searchParams]);

  // Parse selections from URL (semicolon-separated)
  const selections = useMemo(() => {
    const paramKey = mode === 'paths' ? 'paths' : 'majors';
    const param = searchParams.get(paramKey);

    return param ? param.split(';').filter(Boolean) : [];
  }, [searchParams, mode]);

  // Parse primary item from URL
  const primaryItem = useMemo(() => searchParams.get('primary') || null, [searchParams]);

  // Parse visible levels from URL (comma-separated)
  const visibleLevels = useMemo(() => {
    const levels = searchParams.get('levels') || DEFAULT_LEVELS.join(',');
    const parsedLevels = levels
      .split(',')
      .filter((l): l is RelevanceRating => ['High', 'Medium', 'Low'].includes(l));

    return new Set(parsedLevels);
  }, [searchParams]);

  // Parse page number from URL
  const page = useMemo(() => parseInt(searchParams.get('page') || '1', 10), [searchParams]);

  // Fetch available items based on mode
  const itemsType = mode === 'paths' ? 'paths' : 'majors';
  const { data: itemsData, isLoading: isItemsLoading } = useGetGuideItemsQuery({ type: itemsType });

  // Fetch relevance results (only when we have selections)
  const {
    data: relevanceData,
    isLoading: isRelevanceLoading,
    isFetching: isRelevanceFetching,
  } = useGetRelevanceQuery(
    {
      mode,
      selections: selections.join(';'),
      primary: primaryItem || undefined,
      levels: Array.from(visibleLevels).join(','),
      page,
      perPage: ITEMS_PER_PAGE,
    },
    { skip: selections.length === 0 },
  );

  useAgentContext(
    mode,
    selections,
    visibleLevels,
    relevanceData?.total ?? 0,
    relevanceData?.items ?? [],
  );

  // Handle mode change
  const handleModeChange = useCallback(
    (newMode: RelevanceMode) => {
      const updatedParams = new URLSearchParams();

      updatedParams.set('mode', newMode);
      // Clear selections when mode changes
      setSearchParams(updatedParams, { replace: true });
    },
    [setSearchParams],
  );

  // Handle selection changes
  const handleSelectionChange = useCallback(
    (newSelections: string[]) => {
      const updatedParams = new URLSearchParams(searchParams);
      const paramKey = mode === 'paths' ? 'paths' : 'majors';
      const otherParamKey = mode === 'paths' ? 'majors' : 'paths';

      // Remove the other mode's selections
      updatedParams.delete(otherParamKey);

      if (newSelections.length === 0) {
        updatedParams.delete(paramKey);
        updatedParams.delete('primary');
      } else {
        updatedParams.set(paramKey, newSelections.join(';'));

        // If primary was removed from selections, clear it
        const currentPrimary = updatedParams.get('primary');

        if (currentPrimary && !newSelections.includes(currentPrimary)) {
          updatedParams.delete('primary');
        }
      }

      // Reset page when selections change
      updatedParams.delete('page');

      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams, mode],
  );

  // Handle level toggle
  const handleLevelToggle = useCallback(
    (level: RelevanceRating) => {
      const updatedParams = new URLSearchParams(searchParams);
      const newLevels = new Set(visibleLevels);

      if (newLevels.has(level)) {
        newLevels.delete(level);
      } else {
        newLevels.add(level);
      }

      // Don't allow all levels to be deselected
      if (newLevels.size === 0) {
        return;
      }

      updatedParams.set('levels', Array.from(newLevels).join(','));

      // Reset page when levels change
      updatedParams.delete('page');

      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams, visibleLevels],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const updatedParams = new URLSearchParams(searchParams);

      if (newPage === 1) {
        updatedParams.delete('page');
      } else {
        updatedParams.set('page', String(newPage));
      }

      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Handle result click - navigate to detail page
  const handleResultClick = useCallback(
    (item: RelevanceResultItem) => {
      // Ensure item has a valid id before navigating
      if (!item?.id) {
        console.warn('Result item missing id:', item);

        return;
      }

      // When mode is 'paths', results are majors - navigate to /majors/:id
      // When mode is 'majors', results are career paths - navigate to /career-paths/:id
      if (mode === 'paths') {
        navigate(`/majors/${item.id}`);
      } else {
        navigate(`/career-paths/${item.id}`);
      }
    },
    [mode, navigate],
  );

  // Handle reset levels - resets visibility levels back to default (all visible)
  const handleResetLevels = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams);

    // Reset levels to default (removes the param, which defaults to all levels)
    updatedParams.delete('levels');
    // Reset page when levels change
    updatedParams.delete('page');

    setSearchParams(updatedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <CareerRelevancePage
      mode={mode}
      availableItems={itemsData?.items ?? []}
      selectedItems={selections}
      visibleLevels={visibleLevels}
      results={relevanceData?.items ?? []}
      total={relevanceData?.total ?? 0}
      page={page}
      isLoading={isItemsLoading || isRelevanceLoading || isRelevanceFetching}
      isItemsLoading={isItemsLoading}
      onModeChange={handleModeChange}
      onSelectionChange={handleSelectionChange}
      onLevelToggle={handleLevelToggle}
      onPageChange={handlePageChange}
      onResultClick={handleResultClick}
      perPage={ITEMS_PER_PAGE}
      onResetLevels={handleResetLevels}
    />
  );
};
